import type { LoadedModel } from "../viewer/loader";
import type { ModelCatalog, Part } from "./schema";
import type { PartsStore } from "./parts-store";
import type { Basket } from "../basket/store";
import { t } from "../i18n/t";

const SVG_SEARCH = `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="11" cy="11" r="8"/><path d="m21 21-4.3-4.3"/></svg>`;
const SVG_PLUS   = `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M12 5v14M5 12h14"/></svg>`;

export class PartsList {
  private root: HTMLElement;
  private listEl!: HTMLElement;
  private searchInput!: HTMLInputElement;
  private query = "";
  private rowMap = new Map<string, HTMLElement>(); // meshName → row

  constructor(
    container: HTMLElement,
    private catalog: ModelCatalog,
    private model: LoadedModel,
    private store: PartsStore,
    private basket: Basket,
  ) {
    this.root = container;
    this.render();

    this.store.subscribe((s) => this.applyState(s.hoveredPart, s.selectedPart));
  }

  private render() {
    this.root.innerHTML = `
      <div class="kb-parts-search">
        <span class="kb-parts-search-icon">${SVG_SEARCH}</span>
        <input type="search" placeholder="${t("parts.search")}" autocomplete="off" />
      </div>
      <div class="kb-parts-list" role="list"></div>
    `;
    this.listEl = this.root.querySelector(".kb-parts-list") as HTMLElement;
    this.searchInput = this.root.querySelector("input") as HTMLInputElement;

    this.searchInput.addEventListener("input", () => {
      this.query = this.searchInput.value.trim().toLowerCase();
      this.renderList();
    });

    this.renderList();
  }

  private renderList() {
    this.rowMap.clear();
    this.listEl.innerHTML = "";

    const visible = this.catalog.parts
      .filter((p) => !p.hidden)
      .filter((p) => this.matches(p));

    if (visible.length === 0) {
      const empty = document.createElement("div");
      empty.className = "kb-empty-list";
      empty.textContent = this.query
        ? t("parts.empty.search") + `"${this.query}"`
        : t("parts.empty");
      this.listEl.appendChild(empty);
      return;
    }

    // Group rendering
    const groupOrder = (this.catalog.groups ?? [])
      .slice()
      .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
    const seenInGroup = new Set<string>();

    for (const g of groupOrder) {
      const inGroup = visible.filter((p) => p.group === g.id);
      if (inGroup.length === 0) continue;
      const h = document.createElement("div");
      h.className = "kb-group-header";
      h.textContent = g.name.de;
      this.listEl.appendChild(h);
      for (const p of inGroup) {
        seenInGroup.add(p.artNr);
        this.appendRow(p);
      }
    }
    // Any without a known group go at the end
    const ungrouped = visible.filter((p) => !seenInGroup.has(p.artNr));
    if (ungrouped.length > 0) {
      const h = document.createElement("div");
      h.className = "kb-group-header";
      h.textContent = "Weitere";
      this.listEl.appendChild(h);
      for (const p of ungrouped) this.appendRow(p);
    }
  }

  private matches(p: Part): boolean {
    if (!this.query) return true;
    const hay = (
      p.name.de + " " + p.artNr + " " + (p.description?.de ?? "") + " " + (p.group ?? "")
    ).toLowerCase();
    return hay.includes(this.query);
  }

  private appendRow(p: Part) {
    // Skip rows where the catalog entry didn't resolve to any mesh — they
    // would be confusing if the user clicked them and nothing happened.
    if (!this.model.parts.has(p.meshName)) return;

    const row = document.createElement("div");
    row.className = "kb-part";
    row.dataset.mesh = p.meshName;
    row.setAttribute("role", "listitem");
    row.innerHTML = `
      <span class="kb-part-dot" style="background:${p.color ?? "#ccc"}"></span>
      <span class="kb-part-num">${p.artNr.replace("KB-", "")}</span>
      <span class="kb-part-text">
        <span class="kb-part-name">${escapeHtml(p.name.de)}</span>
      </span>
    `;
    row.addEventListener("mouseenter", () => this.store.setHovered(p.meshName));
    row.addEventListener("mouseleave", () => {
      if (this.store.get().hoveredPart === p.meshName) this.store.setHovered(null);
    });
    row.addEventListener("click", (e) => {
      e.stopPropagation();
      const cur = this.store.get().selectedPart;
      this.store.setSelected(cur === p.meshName ? null : p.meshName);
    });

    this.listEl.appendChild(row);
    this.rowMap.set(p.meshName, row);
  }

  private applyState(hovered: string | null, selected: string | null) {
    for (const [meshName, row] of this.rowMap) {
      row.classList.toggle("hovered", meshName === hovered);
      const isSelected = meshName === selected;
      const wasSelected = row.classList.contains("selected");
      row.classList.toggle("selected", isSelected);

      // Remove any leftover detail card from a previous selection
      const next = row.nextElementSibling;
      if (next && next.classList.contains("kb-part-detail")) next.remove();

      if (isSelected) {
        const part = this.catalog.parts.find((p) => p.meshName === meshName);
        if (part) {
          const detail = this.buildDetailCard(part);
          row.parentElement?.insertBefore(detail, row.nextSibling);
        }
        if (!wasSelected) row.scrollIntoView({ block: "nearest", behavior: "smooth" });
      }
    }
  }

  private buildDetailCard(part: Part): HTMLElement {
    const wrap = document.createElement("div");
    wrap.className = "kb-part-detail";
    wrap.innerHTML = `
      <div class="kb-detail-art">${part.artNr}</div>
      <div class="kb-detail-name">${escapeHtml(part.name.de)}</div>
      ${
        part.description
          ? `<div class="kb-detail-desc">${escapeHtml(part.description.de)}</div>`
          : ""
      }
      <div class="kb-detail-stock">${t("parts.stock")}</div>
      <div class="kb-detail-row">
        <div class="kb-qty">
          <button data-act="dec" aria-label="weniger">−</button>
          <input type="number" min="1" max="99" value="1" />
          <button data-act="inc" aria-label="mehr">＋</button>
        </div>
        <button class="kb-add-btn">${SVG_PLUS}<span>${t("parts.add")}</span></button>
      </div>
    `;
    const qty = wrap.querySelector("input") as HTMLInputElement;
    const dec = wrap.querySelector('[data-act="dec"]') as HTMLButtonElement;
    const inc = wrap.querySelector('[data-act="inc"]') as HTMLButtonElement;
    const addBtn = wrap.querySelector(".kb-add-btn") as HTMLButtonElement;

    dec.addEventListener("click", () => {
      const v = Math.max(1, Number(qty.value) - 1);
      qty.value = String(v);
    });
    inc.addEventListener("click", () => {
      const v = Math.min(99, Number(qty.value) + 1);
      qty.value = String(v);
    });
    addBtn.addEventListener("click", () => {
      const q = Math.max(1, Math.min(99, Number(qty.value) || 1));
      this.basket.add(part, q);
      addBtn.querySelector("span")!.textContent = t("parts.added");
      addBtn.style.background = "var(--kb-deep-blue)";
      setTimeout(() => {
        addBtn.querySelector("span")!.textContent = t("parts.add");
        addBtn.style.background = "";
      }, 1100);
    });

    return wrap;
  }
}

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}
